import * as React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import type { PDFViewer } from "pdfjs-dist/web/pdf_viewer.mjs";
import { breiteStufe, vorschauMasse, type PlatzierAuftrag } from "./platzieren";
import { Z_LAYERS } from "../../windowManager/zLayers";
import "./pdfEditor.css";
import { alleFenster } from "../../windowManager/browserFenster/fensterRegister";
import { istDom } from "../../windowManager/browserFenster/domArt";

/**
 * Vorschau beim Platzieren (User 02.10.2026: „damit man weiß, wo sie landen“).
 *
 * Solange ein Auftrag läuft, hängt das Bild halbtransparent am Mauszeiger — in
 * der Größe, die es auf der Seite unter dem Zeiger bekommt — und folgt ihm über
 * Seitenränder hinweg; außerhalb des Viewers verschwindet es. Strg+Mausrad
 * ändert die Breite in Stufen (und zoomt dabei nicht die Seite), Esc bricht ab,
 * der Klick mit der linken Taste setzt. Seitenklicks werden in der
 * Capture-Phase geschluckt, damit PDF.js sie nicht auch als „neuer Text hier“
 * deutet; Rechts-/Mittelklick lassen den Modus stehen.
 *
 * Das Bild liegt als `position: fixed` im `document.body` — der Editor kann in
 * einem Fenster mit CSS-Transformation stecken, dort wäre „fixed“ sonst relativ.
 * Es liegt auf `Z_LAYERS.ueberFenster`, sonst malt die Fensterkarte (Layer 9000)
 * darüber und die Vorschau ist unsichtbar.
 */

/** Steht am scrollenden Container, solange platziert wird (Mauszeiger per CSS). */
export const PLATZIEREN_ATTR = "data-platzieren";

export type PlatzierOrt = { seitenIndex: number; cssX: number; cssY: number };

type SeitenMasse = { breitePt: number; hoehePt: number; cssProPt: number };

/** pt-Maße der Seite und CSS-Pixel je pt — aus dem PDF.js-Viewport der Seite. */
function seitenMasse(viewer: PDFViewer | null, seite: HTMLElement): SeitenMasse | null {
    const nummer = Number(seite.dataset.pageNumber);
    if (!nummer || !viewer) return null;
    const view = viewer.getPageView(nummer - 1) as
        | { viewport?: { scale?: number; rawDims?: { pageWidth: number; pageHeight: number } } }
        | undefined;
    // PDF.js' Viewport-Maßstab ist genau „CSS-Pixel je pt“ (Zoom × 96/72) — sonst aus dem Zoom.
    const cssProPt = view?.viewport?.scale ?? viewer.currentScale * (96 / 72);
    const dims = view?.viewport?.rawDims;
    if (dims) return { breitePt: dims.pageWidth, hoehePt: dims.pageHeight, cssProPt };
    // Ohne Rohmaße (anderer Viewer-Stand): die Seite aus ihrem Div zurückrechnen.
    const r = seite.getBoundingClientRect();
    return { breitePt: r.width / cssProPt, hoehePt: r.height / cssProPt, cssProPt };
}

const seiteVon = (e: Event): HTMLElement | null =>
    istDom(e.target, "Element") ? e.target.closest<HTMLElement>(".page") : null;

export const PlatzierEbene: React.FC<{
    auftrag: PlatzierAuftrag | null;
    viewer: () => PDFViewer | null;
    /** Scrollender Container des Viewers — dort werden Seitenklicks abgefangen. */
    container: HTMLElement | null;
    onBreite: (breitePt: number) => void;
    onSetzen: (ort: PlatzierOrt) => void;
    onAbbruch: () => void;
}> = ({ auftrag, viewer, container, onBreite, onSetzen, onAbbruch }) => {
    const [zeiger, setZeiger] = React.useState<{ x: number; y: number; seite: HTMLElement | null } | null>(null);
    const [bildMasse, setBildMasse] = React.useState<{ w: number; h: number } | null>(null);
    // Die Listener bleiben über Breitenänderungen hinweg stehen — Rückrufe und Auftrag per Ref.
    const auftragRef = React.useRef(auftrag);
    auftragRef.current = auftrag;
    const rueckrufe = React.useRef({ onBreite, onSetzen, onAbbruch });
    rueckrufe.current = { onBreite, onSetzen, onAbbruch };
    const aktiv = auftrag !== null;

    React.useEffect(() => {
        setBildMasse(null);
    }, [auftrag?.url]);

    React.useEffect(() => {
        if (!aktiv || !container) return;
        setZeiger(null);
        container.setAttribute(PLATZIEREN_ATTR, "");
        const schlucken = (e: Event) => {
            if (!seiteVon(e) || (e as MouseEvent).button !== 0) return;
            e.preventDefault();
            e.stopPropagation();
        };
        const setzen = (e: PointerEvent) => {
            const seite = seiteVon(e);
            // Nur die Primärtaste setzt; Rechts-/Mittelklick lassen den Modus stehen.
            if (!seite || e.button !== 0) return;
            e.preventDefault();
            e.stopPropagation();
            const nummer = Number(seite.dataset.pageNumber);
            if (!nummer) return;
            const r = seite.getBoundingClientRect();
            rueckrufe.current.onSetzen({ seitenIndex: nummer - 1, cssX: e.clientX - r.left, cssY: e.clientY - r.top });
        };
        const bewegen = (e: PointerEvent) => {
            const drinnen = istDom(e.target, "Node") && container.contains(e.target);
            setZeiger(drinnen ? { x: e.clientX, y: e.clientY, seite: seiteVon(e) } : null);
        };
        const rad = (e: WheelEvent) => {
            if (!e.ctrlKey && !e.metaKey) return;
            // Nicht die Seite (Browser-Zoom), sondern den Stempel vergrößern.
            e.preventDefault();
            const a = auftragRef.current;
            if (!a || e.deltaY === 0) return;
            rueckrufe.current.onBreite(breiteStufe(a.breitePt, e.deltaY < 0 ? 1 : -1));
        };
        const esc = (e: KeyboardEvent) => {
            if (e.key === "Escape") rueckrufe.current.onAbbruch();
        };
        container.addEventListener("pointerdown", schlucken, true);
        container.addEventListener("pointerup", setzen, true);
        container.addEventListener("click", schlucken, true);
        container.addEventListener("wheel", rad, { passive: false, capture: true });
        alleFenster.addEventListener("pointermove", bewegen);
        alleFenster.addEventListener("keydown", esc);
        return () => {
            container.removeAttribute(PLATZIEREN_ATTR);
            container.removeEventListener("pointerdown", schlucken, true);
            container.removeEventListener("pointerup", setzen, true);
            container.removeEventListener("click", schlucken, true);
            container.removeEventListener("wheel", rad, true);
            alleFenster.removeEventListener("pointermove", bewegen);
            alleFenster.removeEventListener("keydown", esc);
        };
    }, [aktiv, container]);

    if (!auftrag || !zeiger) return null;
    const v = viewer();
    // Zwischen zwei Seiten gilt die Größe der angezeigten Seite.
    const seite =
        zeiger.seite ??
        ((v?.getPageView(Math.max(0, (v.currentPageNumber || 1) - 1)) as { div?: HTMLElement } | undefined)?.div ?? null);
    const masse = seite ? seitenMasse(v, seite) : null;
    const { w, h } = masse ? vorschauMasse(auftrag.breitePt, bildMasse, masse) : { w: auftrag.breitePt * (96 / 72), h: null };
    return createPortal(
        <img
            src={auftrag.url}
            alt=""
            aria-hidden
            draggable={false}
            className="pdfx pdfx-vorschau"
            data-platzier-vorschau=""
            style={{ left: zeiger.x, top: zeiger.y, width: w, height: h ?? "auto", zIndex: Z_LAYERS.ueberFenster }}
            onLoad={(e) => {
                const img = e.currentTarget;
                if (img.naturalWidth > 0 && img.naturalHeight > 0) setBildMasse({ w: img.naturalWidth, h: img.naturalHeight });
            }}
        />,
        document.body,
    );
};
